import React, { useState, useEffect } from 'react';
import { useLazyCommand, useEvent, useApiErrors } from '@soap/modules';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { ThinkingAnimation } from './ThinkingAnimation';
import { Dialog, DialogContent } from '../ui/dialog';
import { getTopicId, getTopicName } from './LessonsPage/utils/topicUtils';

interface CreateAssessmentFormProps {
  showForm: boolean;
  onClose: () => void;
  selectedTopics?: any[];
  unitId?: string;
  topicIds?: string[];
  studentId?: string;
  unitTitle?: string;
  onCreateAssessment?: () => void;
  buildBreadcrumb?: (topic: any) => string;
  onRemoveTopic?: (index: number) => void;
  hasAttemptedSubmit?: boolean;
}

const timingModes = {
  'not-timed': 'Not Timed',
  'per-question': 'Per Question',
  'per-section': 'Per Section',
  'per-assessment': 'Per Assessment'
};

const questionTypes = {
  'multiple-choice': 'Multiple Choice',
  'boolean': 'Boolean',
  'freetext': 'FreeText',
  'single-selection': 'Single Selection',
  'specific-string': 'Specific String'
};

export function CreateAssessmentForm({
  showForm,
  onClose,
  selectedTopics = [],
  unitId,
  topicIds = [],
  studentId,
  unitTitle,
  onCreateAssessment,
  buildBreadcrumb,
  onRemoveTopic,
  hasAttemptedSubmit = false
}: CreateAssessmentFormProps) {
  const [sendCreateAssessmentCommand] = useLazyCommand('C274v1_CreateAssessment');
  
  const [assessmentTitle, setAssessmentTitle] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [timingMode, setTimingMode] = useState('not-timed');
  const [questionsPerTopicInput, setQuestionsPerTopicInput] = useState('10');
  const [timePerQuestionMinutes, setTimePerQuestionMinutes] = useState(0.5); // Default: 30 seconds (0.5 minutes)
  
  // Convert string input to number for calculations
  const questionsPerTopic = parseInt(questionsPerTopicInput, 10) || 0;
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<string[]>(['multiple-choice', 'boolean']);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isCreating, setIsCreating] = useState<boolean>(false);

  // Use useApiErrors to handle errors - if we're in creating state, close the loading dialog
  useApiErrors(() => {
    // If we're in creating state and an error occurs, close the loading dialog
    // We don't need to check the message type since this form only sends one command type
    if (isCreating) {
      setIsCreating(false);
    }
  });

  // Handle form opening/closing state
  useEffect(() => {
    if (showForm) {
      // When form opens, set default title if provided and title is currently empty
      if (unitTitle && unitTitle.trim()) {
        // Use unitTitle if provided (for assessments linked to existing units)
        setAssessmentTitle(unitTitle);
      } else if (!unitId && selectedTopics && selectedTopics.length > 0) {
        // For assessment-only units, use topic names joined by commas
        const topicNames = selectedTopics
          .map(topic => getTopicName(topic))
          .filter((name): name is string => !!name)
          .join(', ');
        if (topicNames) {
          setAssessmentTitle(topicNames);
        }
      }
    } else {
      // Reset form when closed
      setIsCreating(false);
      setAssessmentTitle('');
      setDescription('');
      setInstructions('');
      setQuestionsPerTopicInput('10');
      setTimePerQuestionMinutes(0.5); // Default: 30 seconds (0.5 minutes)
      setErrors({});
    }
  }, [showForm, unitTitle, unitId, selectedTopics]);

  // Listen for assessment created event to reset creating state
  useEvent({
    eventName: 'E264v1_AssessmentCreated',
    onEventReceived: () => {
      setIsCreating(false);
      if (onCreateAssessment) {
        onCreateAssessment();
      }
      // For assessment-only units (no unitId), navigate to in-progress tab
      if (!unitId) {
        window.location.hash = '#/app/lessons/inprogress';
      }
    }
  });

  if (!showForm) return null;

  if (isCreating) {
    return (
      <Dialog open onOpenChange={() => {}}>
        <DialogContent dismissible={false} className="max-w-2xl sm:max-w-2xl">
          <ThinkingAnimation message="Creating your Assessment..." />
        </DialogContent>
      </Dialog>
    );
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!assessmentTitle.trim()) {
      newErrors.title = 'Assessment title is required';
    }
    
    if (questionsPerTopic < 1 || questionsPerTopic > 20) {
      newErrors.questionsPerTopic = 'Questions per topic must be between 1 and 20';
    }
    
    if (selectedQuestionTypes.length === 0) {
      newErrors.questionTypes = 'At least one question type must be selected';
    }

    const topicsToUse = topicIds.length > 0 ? topicIds : (selectedTopics?.map(t => getTopicId(t)).filter((id): id is string => !!id) || []);
    if (topicsToUse.length === 0) {
      newErrors.topics = 'At least one topic must be selected';
    }

    if (!studentId) {
      newErrors.student = 'Student ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsCreating(true);

    // Convert topicIds - they come as strings from UnitDto but backend expects List<Guid>
    // SOAP framework will handle string-to-Guid conversion automatically
    const topicsToUse = topicIds.length > 0 
      ? topicIds.filter(id => id) // Keep as strings, framework converts
      : (selectedTopics?.map(t => getTopicId(t)).filter((id): id is string => !!id) || []);
    
    // StudentId - framework handles conversion
    const studentGuid = studentId;
    
    const totalTopics = topicIds.length > 0 ? topicIds.length : (selectedTopics?.length || 0);
    let timePerQuestionSeconds: number | null = null;
    let timePerSectionSeconds: number | null = null;
    let overallTimeLimitSeconds: number | null = null;
    
    if (timingMode !== 'not-timed') {
      timePerQuestionSeconds = timePerQuestionMinutes * 60;
      
      if (timingMode === 'per-section' || timingMode === 'per-assessment') {
        timePerSectionSeconds = timePerQuestionSeconds * questionsPerTopic;
      }
      
      if (timingMode === 'per-assessment' && totalTopics > 0) {
        overallTimeLimitSeconds = timePerQuestionSeconds * questionsPerTopic * totalTopics;
      }
    }

    await sendCreateAssessmentCommand({
      C274_AssessmentTitle: assessmentTitle,
      C274_Description: description,
      C274_Instructions: instructions,
      C274_AssessmentTimingModeKey: timingMode,
      C274_QuestionsPerSection: questionsPerTopic,
      C274_QuestionTypes: selectedQuestionTypes,
      C274_TopicIds: topicsToUse,
      C274_UnitId: unitId || '',
      C274_StudentId: studentGuid,
      C274_TimePerQuestionSeconds: timePerQuestionSeconds,
      C274_TimePerSectionSeconds: timePerSectionSeconds,
      C274_OverallTimeLimitSeconds: overallTimeLimitSeconds
    });

    // Note: The creating state will be reset when E264v1_AssessmentCreated event is received
  };

  const toggleQuestionType = (type: string) => {
    setSelectedQuestionTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Create Assessment</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* Selected Topics (if from tree view) */}
            {selectedTopics && selectedTopics.length > 0 && (
              <div>
                <Label>Selected Topics ({selectedTopics.length})</Label>
                <div className="space-y-2 mt-2">
                  {selectedTopics.map((topic, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{getTopicName(topic)}</div>
                        {buildBreadcrumb && (
                          <div className="text-sm text-gray-500 mt-1">{buildBreadcrumb(topic)}</div>
                        )}
                      </div>
                      {onRemoveTopic && (
                        <button
                          onClick={() => onRemoveTopic(index)}
                          className="text-gray-400 hover:text-destructive-action transition-colors ml-3"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {errors.topics && <p className="text-destructive-action text-sm mt-1">{errors.topics}</p>}
              </div>
            )}

            {/* Assessment Title */}
            <div>
              <Label htmlFor="title">Assessment Title *</Label>
              <Input
                id="title"
                value={assessmentTitle}
                onChange={(e) => setAssessmentTitle(e.target.value)}
                placeholder="Enter assessment title"
                className={errors.title ? 'border-destructive-action' : ''}
              />
              {errors.title && <p className="text-destructive-action text-sm mt-1">{errors.title}</p>}
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter assessment description"
                rows={3}
              />
            </div>

            {/* Instructions */}
            <div>
              <Label htmlFor="instructions">Instructions</Label>
              <Textarea
                id="instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Enter instructions for the assessment"
                rows={4}
              />
            </div>

            {/* Assessment Timing Mode */}
            <div className="relative">
              <Label htmlFor="timingMode">Assessment Timing Mode</Label>
              <Select value={timingMode} onValueChange={setTimingMode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select timing mode" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(timingModes).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time Configuration - shown only for timed modes */}
            {timingMode !== 'not-timed' && (() => {
              const totalTopics = topicIds.length > 0 ? topicIds.length : (selectedTopics?.length || 0);
              const timePerQuestionSeconds = timePerQuestionMinutes * 60;
              const timePerSectionSeconds = timePerQuestionSeconds * questionsPerTopic;
              const overallTimeLimitSeconds = timePerQuestionSeconds * questionsPerTopic * totalTopics;
              
              return (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {/* Note spanning both columns */}
                  <p className="text-xs text-gray-600 italic mb-4">
                    Note: Individual time values per question, section, or assessment can be adjusted by editing the assessment after creation.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Column 1: Time per Question */}
                    <div>
                      <Label htmlFor="timePerQuestion">Time per Question (minutes) *</Label>
                      <Input
                        id="timePerQuestion"
                        type="number"
                        min="0.5"
                        step="0.5"
                        value={timePerQuestionMinutes}
                        onChange={(e) => setTimePerQuestionMinutes(parseFloat(e.target.value) || 0.5)}
                        className="mt-1"
                      />
                    </div>
                    
                    {/* Column 2: Time per Section or Time per Assessment */}
                    <div>
                      {timingMode === 'per-section' && (
                        <>
                          <Label>Time per Section (calculated)</Label>
                          <Input
                            type="text"
                            value={`${(timePerSectionSeconds / 60).toFixed(1)} minutes`}
                            disabled
                            className="mt-1 bg-gray-100"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {timePerQuestionMinutes} min/question × {questionsPerTopic} questions = {(timePerSectionSeconds / 60).toFixed(1)} minutes
                          </p>
                        </>
                      )}
                      
                      {timingMode === 'per-assessment' && totalTopics > 0 && (
                        <>
                          <Label>Time per Assessment (calculated)</Label>
                          <Input
                            type="text"
                            value={`${(overallTimeLimitSeconds / 60).toFixed(1)} minutes`}
                            disabled
                            className="mt-1 bg-gray-100"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {timePerQuestionMinutes} min/question × {questionsPerTopic} questions × {totalTopics} topics = {(overallTimeLimitSeconds / 60).toFixed(1)} minutes
                          </p>
                        </>
                      )}
                      
                      {timingMode === 'per-question' && (
                        <div className="text-sm text-gray-500 mt-1">
                          Each question will have {timePerQuestionMinutes} minute{timePerQuestionMinutes !== 1 ? 's' : ''} time limit.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Questions per Topic */}
            <div>
              <Label htmlFor="questionsPerTopic">Questions per Topic *</Label>
              <Input
                id="questionsPerTopic"
                type="number"
                min="1"
                max="20"
                value={questionsPerTopicInput}
                onChange={(e) => {
                  setQuestionsPerTopicInput(e.target.value);
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (isNaN(value) || value < 1 || value > 20) {
                    setQuestionsPerTopicInput('10');
                  } else {
                    setQuestionsPerTopicInput(value.toString());
                  }
                }}
                className={errors.questionsPerTopic ? 'border-destructive-action' : ''}
              />
              {errors.questionsPerTopic && <p className="text-destructive-action text-sm mt-1">{errors.questionsPerTopic}</p>}
              {(() => {
                const totalTopics = topicIds.length > 0 ? topicIds.length : (selectedTopics?.length || 0);
                const totalQuestions = totalTopics * questionsPerTopic;
                return totalTopics > 0 ? (
                  <p className="text-sm text-gray-600 mt-1">Total Questions: {totalQuestions}</p>
                ) : null;
              })()}
            </div>

            {/* Question Types */}
            <div>
              <Label>Question Types *</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {Object.entries(questionTypes).map(([key, label]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={selectedQuestionTypes.includes(key)}
                      onCheckedChange={() => toggleQuestionType(key)}
                    />
                    <Label htmlFor={key} className="font-normal cursor-pointer">{label}</Label>
                  </div>
                ))}
              </div>
              {errors.questionTypes && <p className="text-destructive-action text-sm mt-1">{errors.questionTypes}</p>}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>
                Create Assessment
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
